import { Button } from "@chakra-ui/react"
import {
  CoverPageOfferFragment,
  OfferAccessState
} from "@pathwright/graphql-client/src/generated"
import { Link as ReactRouterLink } from "react-router-dom"
import CoverPageContinueButton from "./CoverPageContinueButton"
import { useCoverPageHasLoadedResource } from "./useCoverPageHasLoadedResource"
import { OfferButtonRenderProps } from "./CoverPageOffer"
import { useCoverPageContext } from "./CoverPageProvider"
import {
  getCoverPageAnotherActionLabels,
  getPrivateCohortAnotherAction
} from "./coverPageAnotherAction"
import { getOfferContinueOptions } from "./coverPageOfferOrdering"
import { useCoverPageContinue } from "./useCoverPageContinue"
import { useCreatePathFromOffer } from "./useCreatePathFromOffer"
import { useCoverPageTranslate } from "./utils"

function stopCardNavigation(e: React.MouseEvent) {
  // Prevent the card wrapper from opening the offer immersive route.
  e.stopPropagation()
}

// For licensing a path from another school (via store flow).
// On the licensor school, open /sign-up/?license=… in a new tab.
// On a connected licensee space, license in place via createCurriculumLicenseOrder.
// When the signed-in user already has access on this resource, show Continue.
// NOTE: in-place licensing assumes the offer can be used without payment or
// further input. Paid license checkout is still to do.
function CoverPagePathOfferButton({
  offer,
  renderButton,
  ...buttonProps
}: {
  offer: CoverPageOfferFragment
  renderButton?: (props: OfferButtonRenderProps) => React.ReactNode
} & OfferButtonRenderProps) {
  const { t } = useCoverPageTranslate()
  const hasLoadedResource = useCoverPageHasLoadedResource()
  const createPath = useCreatePathFromOffer(offer)
  const resourceId = useCoverPageContext((v) => v.resourceId)
  const continueState = useCoverPageContinue(
    resourceId,
    getOfferContinueOptions(offer)
  )

  // Continue takes priority over create once access is known.
  if (continueState.ready && continueState.mode !== "none") {
    const anotherAction = getPrivateCohortAnotherAction({
      offer,
      accesses: continueState.accesses
    })
    const anotherLabels = getCoverPageAnotherActionLabels(anotherAction.kind)
    return (
      <CoverPageContinueButton
        accesses={continueState.accesses}
        canCreateAnother={anotherAction.show}
        anotherActionLabel={
          anotherLabels
            ? t(anotherLabels.ellipsisKey, {
                defaultValue: anotherLabels.ellipsisDefault
              })
            : undefined
        }
        onCreateAnother={anotherAction.show ? createPath.create : undefined}
        createAnotherLoading={createPath.loading}
        buttonProps={buttonProps}
        renderButton={renderButton}
      />
    )
  }

  // Default create / view-path flow (no known RegistrationAccess yet).
  const passedButtonProps: OfferButtonRenderProps =
    !hasLoadedResource && !createPath.newResourceLink
      ? {
          ...buttonProps,
          as: undefined,
          to: undefined,
          href: undefined,
          relativeTo: undefined,
          disabled: true,
          onClick: undefined,
          onPointerDown: undefined,
          _disabled: { ...buttonProps._disabled, pointerEvents: "auto" }
        }
      : createPath.canPlaceOrder
      ? createPath.newResourceLink
        ? {
            ...buttonProps,
            children: createPath.viewPathLabel,
            disabled: false,
            to: "/home/",
            as: ReactRouterLink
          }
        : {
            ...buttonProps,
            loading: createPath.loading,
            loadingText: createPath.loadingText,
            disabled:
              createPath.loading ||
              offer.access.state === OfferAccessState.Unavailable,
            onClick: createPath.newResourceLink ? undefined : createPath.create,
            to: undefined,
            as: undefined
          }
      : {
          ...buttonProps,
          as: "a",
          href: createPath.signupHref,
          target: "_blank",
          rel: "noopener noreferrer",
          to: undefined,
          relativeTo: undefined,
          onClick: stopCardNavigation
        }

  return renderButton ? (
    renderButton(passedButtonProps)
  ) : (
    <Button {...passedButtonProps} />
  )
}

export default CoverPagePathOfferButton
